/**
 * Mutual Fund Recorder - Investment Balance Recording
 * Manual input form with image upload for investment screenshots
 */

import React, { useState, useRef, useEffect } from 'react';
import { Company, Transaction } from '../../types';
import { Card, Button, Input, Select } from '../UI';
import { useTitle, PAGE_TITLES } from '../../hooks';
import { uploadsApi, transactionsApi, UploadedFile } from '../../services/api.service';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Check, 
  Building, 
  Upload, 
  Image as ImageIcon,
  Trash2,
  FileText, 
  X,
  PieChart,
  Calendar,
  Eye,
  Hash,
  ArrowRight,
  Loader2,
  Edit3,
  AlertTriangle,
  RefreshCw,
  ArrowUpCircle,
  ArrowDownCircle,
  Landmark,
  Wallet
} from 'lucide-react';

// Currency symbol mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  'IDR': 'Rp',
  'USD': '$',
  'EUR': '€',
  'SGD': 'S$',
  'JPY': '¥',
  'GBP': '£',
  'AUD': 'A$',
  'CNY': '¥',
  'MYR': 'RM',
  'THB': '฿',
};

interface UploadedImage {
  id: string;
  name: string;
  previewUrl: string;
  uploadedAt: Date;
  companyId: string;
  investmentAccountId?: string;
}

interface RejectedTransaction {
  transaction_id: string;
  company_id: string;
  account_id: string | null;
  user_id: string | null;
  date: string;
  description: string;
  amount: number;
  type: 'CREDIT' | 'DEBIT';
  status: string;
  imageUrl: string | null;
  createdAt: string;
  company?: { name: string };
  bankAccount?: { bankName: string; accountNumber: string; accountName?: string };
}

interface MutualFundRecorderProps {
  companies: Company[];
  selectedCompanyId: string;
  onSelectCompany: (id: string) => void;
  onSave: (transaction: Transaction) => void;
}

export const MutualFundRecorder: React.FC<MutualFundRecorderProps> = ({ 
  companies, 
  selectedCompanyId, 
  onSelectCompany,
  onSave
}) => {
  useTitle(PAGE_TITLES.MUTUAL_FUND_RECORDER);
  const { user } = useAuth();

  // Manual Form State
  const [formData, setFormData] = useState({
    date: '',
    description: '',
    amount: '',
    unitCount: '',
    type: 'debit' as 'credit' | 'debit',
    bankAccountId: ''
  });

  // Upload State
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState<UploadedImage | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Rejected transactions state
  const [rejectedTransactions, setRejectedTransactions] = useState<RejectedTransaction[]>([]);
  const [isLoadingRejected, setIsLoadingRejected] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<RejectedTransaction | null>(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    description: '',
    amount: '',
    unitCount: '',
    bankAccountId: ''
  });
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [editImageUrl, setEditImageUrl] = useState<string | null>(null);
  const [isUploadingEditImage, setIsUploadingEditImage] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-select first company on mount
  useEffect(() => {
    if (!selectedCompanyId && companies.length > 0) {
      onSelectCompany(companies[0].id);
    }
  }, [companies, selectedCompanyId, onSelectCompany]);

  // Reset bank account when company changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, bankAccountId: '' }));
  }, [selectedCompanyId]);

  // Fetch rejected transactions for current user (only MUTUAL_FUND source)
  const fetchRejectedTransactions = async () => {
    if (!user) return;
    
    setIsLoadingRejected(true);
    try {
      const response = await transactionsApi.getAll({ status: 'FLAGGED', user_id: user.user_id, source: 'MUTUAL_FUND' });
      const data = response?.data || [];
      setRejectedTransactions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch rejected transactions:', err);
    } finally {
      setIsLoadingRejected(false);
    }
  };

  useEffect(() => {
    fetchRejectedTransactions();
  }, [user]);

  // Handle edit transaction
  const handleEditClick = (tx: RejectedTransaction) => {
    setEditingTransaction(tx);
    // Extract unit count from description if present (format: "description (Unit: X)")
    const unitMatch = tx.description.match(/\(Unit:\s*([\d.,]+)\)/);
    const unitCount = unitMatch ? unitMatch[1] : '';
    const cleanDescription = tx.description.replace(/\s*\(Unit:\s*[\d.,]+\)/, '').trim();
    
    setEditFormData({
      date: tx.date.split('T')[0],
      description: cleanDescription,
      amount: tx.amount.toString(),
      unitCount: unitCount,
      bankAccountId: tx.account_id || ''
    });
    setEditImageUrl(tx.imageUrl); // Set current image
  };

  // Handle image upload for edit modal
  const handleEditImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      alert('Hanya file gambar yang didukung.');
      return;
    }

    setIsUploadingEditImage(true);
    try {
      const uploadedFiles = await uploadsApi.upload('mutual-funds', [file]);
      if (uploadedFiles.length > 0) {
        setEditImageUrl(uploadedFiles[0].url);
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
      alert('Gagal mengunggah gambar.');
    } finally {
      setIsUploadingEditImage(false);
      if (editFileInputRef.current) {
        editFileInputRef.current.value = '';
      }
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTransaction) return;

    // Reconstruct description with unit count if provided
    const finalDescription = editFormData.unitCount 
      ? `${editFormData.description} (Unit: ${editFormData.unitCount})`
      : editFormData.description;

    setIsSubmittingEdit(true);
    try {
      await transactionsApi.update(editingTransaction.transaction_id, {
        date: editFormData.date,
        description: finalDescription,
        amount: parseFloat(editFormData.amount),
        type: 'DEBIT', // Mutual funds are always recorded as debit (investment value)
        bankAccountId: editFormData.bankAccountId || undefined,
        status: 'PENDING', // Resubmit as pending
        imageUrl: editImageUrl || undefined,
        units: editFormData.unitCount ? parseFloat(editFormData.unitCount) : undefined
      });
      showSuccess('Transaksi berhasil diperbarui dan diajukan ulang!');
      setEditingTransaction(null);
      setEditImageUrl(null);
      fetchRejectedTransactions(); // Refresh list
    } catch (err) {
      console.error('Failed to update transaction:', err);
      alert('Gagal memperbarui transaksi. Silakan coba lagi.');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  // Group companies for dropdown
  const getCompanyOptions = () => {
    const holdings = companies.filter(c => c.isHolding);
    const independents = companies.filter(c => !c.parentId && !c.isHolding);
    
    const renderOption = (c: Company, depth: number = 0) => (
      <option key={c.id} value={c.id}>
         {'\u00A0'.repeat(depth * 4)} {depth > 0 ? '↳ ' : ''}{c.name}
      </option>
    );

    let options: React.ReactNode[] = [];
    holdings.forEach(holding => {
        options.push(renderOption(holding));
        const children = companies.filter(c => c.parentId === holding.id);
        children.forEach(child => options.push(renderOption(child, 1)));
    });
    if (independents.length > 0) {
        if (holdings.length > 0) options.push(<option key="div" disabled>──────────</option>);
        independents.forEach(ind => options.push(renderOption(ind)));
    }
    return options;
  };

  const getCompanyAccounts = (companyId: string) => {
    // Only return INVESTMENT type accounts for Mutual Fund Recorder
    const allAccounts = companies.find(c => c.id === companyId)?.bankAccounts || [];
    return allAccounts.filter(account => account.accountType === 'INVESTMENT');
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFiles = async (files: File[]) => {
    if (!selectedCompanyId) {
      alert('Silakan pilih perusahaan terlebih dahulu.');
      return;
    }

    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      alert('Hanya file gambar yang didukung (PNG, JPG).');
      return;
    }

    setIsUploading(true);
    
    try {
      // Upload to backend
      const uploadedFiles = await uploadsApi.upload('mutual-funds', imageFiles);
      
      // Create local preview entries
      const newImages: UploadedImage[] = uploadedFiles.map(file => ({
        id: file.fileName,
        name: file.originalName,
        previewUrl: file.url,
        uploadedAt: new Date(file.uploadedAt),
        companyId: selectedCompanyId,
        investmentAccountId: formData.bankAccountId || undefined
      }));
      
      setUploadedImages(prev => [...newImages, ...prev]);
      showSuccess(`${imageFiles.length} gambar berhasil diunggah!`);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Gagal mengunggah gambar. Silakan coba lagi.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(Array.from(files));
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = (id: string) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompanyId) {
      alert("Silakan pilih perusahaan terlebih dahulu.");
      return;
    }
    
    if (!formData.bankAccountId) {
      alert("Silakan pilih produk reksadana terlebih dahulu.");
      return;
    }
    
    const amountNum = parseFloat(formData.amount);
    if (isNaN(amountNum)) return;

    // Parse units if provided
    const unitsNum = formData.unitCount ? parseFloat(formData.unitCount) : undefined;

    // Get the most recent uploaded image for this transaction
    const latestImage = uploadedImages.find(img => img.companyId === selectedCompanyId);
    const imageUrl = latestImage?.previewUrl;

    onSave({
      id: `mf-${Date.now()}`,
      companyId: selectedCompanyId,
      bankAccountId: formData.bankAccountId,
      date: formData.date,
      description: formData.description,
      amount: amountNum,
      units: unitsNum,
      type: formData.type,
      status: 'pending',
      imageUrl: imageUrl
    });

    // Remove the used image from uploadedImages
    if (latestImage) {
      setUploadedImages(prev => prev.filter(img => img.id !== latestImage.id));
    }

    showSuccess('Data Reksadana berhasil direkam!');
    setFormData({ date: '', description: '', amount: '', unitCount: '', type: 'debit', bankAccountId: formData.bankAccountId });
  };

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const getCompanyName = (companyId: string) => {
    return companies.find(c => c.id === companyId)?.name || 'Unknown';
  };

  const getInvestmentAccountName = (companyId: string, accountId?: string) => {
    if (!accountId) return 'Tidak Ditentukan';
    const company = companies.find(c => c.id === companyId);
    const account = company?.bankAccounts?.find(b => b.id === accountId);
    return account ? `${account.bankName} - ${account.accountName}` : 'Unknown';
  };

  const activeCompanyAccounts = getCompanyAccounts(selectedCompanyId);

  // Get currency symbol for the selected bank account
  const selectedCurrency = React.useMemo(() => {
    if (!formData.bankAccountId) return { code: 'IDR', symbol: 'Rp' };
    const account = activeCompanyAccounts.find(a => a.id === formData.bankAccountId);
    const code = account?.currency || 'IDR';
    return { code, symbol: CURRENCY_SYMBOLS[code] || code };
  }, [formData.bankAccountId, activeCompanyAccounts]);

  return (
    <div className="space-y-8">
      
      {/* Success Notification */}
      {successMessage && (
        <div className="fixed top-24 right-8 z-50 animate-fade-in-down">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl shadow-lg flex items-center">
            <div className="bg-emerald-100 rounded-full p-1 mr-3">
                <Check className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column: Manual Entry Form - shows second on mobile, first on desktop */}
        <div className="lg:col-span-7 space-y-6 order-2 lg:order-1">
          <Card className="p-6 sm:p-8 h-full">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Input Manual Reksadana</h2>
                <p className="text-sm text-gray-500 mt-1">Catat Nilai Aktiva Bersih (NAB) harian.</p>
              </div>
            </div>

            <form onSubmit={handleManualSubmit}>
              <Select
                label="Nama Perusahaan"
                startIcon={Building}
                value={selectedCompanyId}
                onChange={(e) => onSelectCompany(e.target.value)}
              >
                <option value="" disabled>Pilih perusahaan...</option>
                {getCompanyOptions()}
              </Select>

              <Select
                label="Produk Reksadana"
                startIcon={PieChart}
                value={formData.bankAccountId}
                onChange={(e) => setFormData({...formData, bankAccountId: e.target.value})}
                disabled={!selectedCompanyId}
              >
                <option value="">Pilih Produk Investasi...</option>
                {activeCompanyAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.bankName} - {acc.accountName}</option>
                ))}
              </Select>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5">
                <Input
                  label="Tanggal Saldo"
                  type="date"
                  startIcon={Calendar}
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
                
                {/* Spacer */}
                <div className="hidden sm:block"></div>

                <Input
                  label={`Total Nilai Investasi (${selectedCurrency.code})`}
                  type="number"
                  step="0.01"
                  min="0"
                  startText={selectedCurrency.symbol}
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  required
                  className="font-mono"
                />
                <Input
                  label="Jumlah Unit"
                  type="number"
                  step="0.0001"
                  min="0"
                  startIcon={Hash}
                  value={formData.unitCount}
                  onChange={(e) => setFormData({...formData, unitCount: e.target.value})}
                  className="font-mono"
                  placeholder="0.0000"
                />
              </div>

              <Input
                label="Keterangan"
                startIcon={FileText}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
              />

              <div className="pt-2">
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full"
                  disabled={!selectedCompanyId || !formData.bankAccountId || !formData.date || !formData.amount || !formData.description || uploadedImages.length === 0}
                >
                  Simpan Data Investasi <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </Card>
        </div>

        {/* Right Column: Upload Area - shows first on mobile, second on desktop */}
        <div className="lg:col-span-5 space-y-6 order-1 lg:order-2">
          <div className="bg-linear-to-br from-emerald-600 to-teal-700 rounded-2xl p-5 text-white shadow-xl shadow-emerald-200 flex flex-col">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                <FileText className="w-5 h-5 text-yellow-300" />
              </div>
              <div>
                <h3 className="font-bold text-base">Unggah Screenshot Portofolio</h3>
              </div>
            </div>
            
            <p className="text-sm text-emerald-50 mb-4 leading-relaxed opacity-90">
              Geser dan letakkan tangkapan layar aplikasi investasi atau laporan NAB Anda di sini.
            </p>
            
            {/* Drag & Drop Area */}
            <div 
              className="relative"
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input 
                 type="file" 
                 ref={fileInputRef}
                 className="hidden" 
                 accept="image/*"
                 multiple
                 onChange={handleFileUpload}
              />
              
              <div 
                 onClick={() => fileInputRef.current?.click()}
                 className={`
                   flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200
                   ${dragActive 
                       ? 'border-yellow-400 bg-white/20 scale-105' 
                       : 'border-emerald-300/30 bg-emerald-800/20 hover:bg-emerald-800/30'}
                 `}
              >
                 <Upload className={`w-10 h-10 mx-auto mb-3 transition-colors ${dragActive ? 'text-yellow-300' : 'text-emerald-300'}`} />
                 <p className="text-sm text-white font-medium">Klik atau Geser Gambar ke Sini</p>
                 <p className="text-xs text-emerald-300 mt-1">Mendukung PNG, JPG</p>
              </div>
            </div>

            {/* Uploaded Images Gallery - inside upload card */}
            {uploadedImages.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-white/90 flex items-center">
                    <div className="w-2 h-2 rounded-full bg-yellow-400 mr-2"></div>
                    Terunggah ({uploadedImages.length})
                  </h4>
                  <button 
                    onClick={() => {
                      if (confirm('Hapus semua gambar?')) {
                        setUploadedImages([]);
                      }
                    }}
                    className="text-xs text-white/60 hover:text-red-300 transition-colors"
                  >
                    Hapus Semua
                  </button>
                </div>
                
                <div className="grid grid-cols-4 gap-2">
                  {uploadedImages.map((img) => (
                    <div 
                      key={img.id} 
                      className="group relative bg-white/10 rounded-lg overflow-hidden"
                    >
                      {/* Image Preview */}
                      <div 
                        className="aspect-square cursor-pointer"
                        onClick={() => setPreviewImage(img)}
                      >
                        <img 
                          src={img.previewUrl} 
                          alt={img.name}
                          className="w-full h-full object-cover"
                        />
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Eye className="w-4 h-4 text-white" />
                        </div>
                      </div>

                      {/* Delete Button */}
                      <button 
                        onClick={() => handleDeleteImage(img.id)}
                        className="absolute top-0.5 right-0.5 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <Trash2 className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-black/80"
            onClick={() => setPreviewImage(null)}
          />
          <div className="relative max-w-4xl max-h-[90vh] overflow-auto bg-white rounded-2xl shadow-2xl">
            <button 
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <img 
              src={previewImage.previewUrl} 
              alt={previewImage.name}
              className="max-w-full max-h-[80vh] object-contain"
            />
            <div className="p-4 border-t">
              <p className="font-medium text-gray-900">{previewImage.name}</p>
              <p className="text-sm text-gray-500">
                {getCompanyName(previewImage.companyId)} • {getInvestmentAccountName(previewImage.companyId, previewImage.investmentAccountId)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Diunggah: {previewImage.uploadedAt.toLocaleString('id-ID')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rejected Transactions Table */}
      {rejectedTransactions.length > 0 && (
        <Card className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Transaksi Ditolak</h3>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Transaksi yang perlu diperbaiki dan diajukan ulang</p>
              </div>
            </div>
            <button
              onClick={fetchRejectedTransactions}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors touch-target"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${isLoadingRejected ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Mobile Card Layout */}
          <div className="sm:hidden space-y-3">
            {rejectedTransactions.map(tx => {
              const unitMatch = tx.description.match(/\(Unit:\s*([\d.,]+)\)/);
              const unitCount = unitMatch ? unitMatch[1] : '-';
              const cleanDescription = tx.description.replace(/\s*\(Unit:\s*[\d.,]+\)/, '').trim();
              
              return (
                <div key={tx.transaction_id} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-gray-500">
                          {new Date(tx.date).toLocaleDateString('id-ID')}
                        </span>
                      </div>
                      <p className="font-semibold text-gray-900 text-sm truncate">{tx.company?.name || '-'}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {tx.bankAccount ? `${tx.bankAccount.bankName} - ${tx.bankAccount.accountName || tx.bankAccount.accountNumber}` : '-'}
                      </p>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{cleanDescription}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-gray-500">Unit: <span className="font-mono font-medium text-gray-700">{unitCount}</span></span>
                        <span className="font-bold text-gray-900">
                          {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(tx.amount)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {tx.imageUrl ? (
                        <a href={tx.imageUrl} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={tx.imageUrl} 
                            alt="Bukti" 
                            className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                          />
                        </a>
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                          <ImageIcon className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <button
                        onClick={() => handleEditClick(tx)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium text-xs touch-target"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Tanggal</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Perusahaan</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Produk Reksadana</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Keterangan</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Bukti</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Jumlah Unit</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Total Nilai</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rejectedTransactions.map(tx => {
                  // Extract unit count from description if present (format: "description (Unit: X)")
                  const unitMatch = tx.description.match(/\(Unit:\s*([\d.,]+)\)/);
                  const unitCount = unitMatch ? unitMatch[1] : '-';
                  const cleanDescription = tx.description.replace(/\s*\(Unit:\s*[\d.,]+\)/, '').trim();
                  
                  return (
                    <tr key={tx.transaction_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600">
                        {new Date(tx.date).toLocaleDateString('id-ID')}
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-medium">
                        {tx.company?.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {tx.bankAccount ? `${tx.bankAccount.bankName} - ${tx.bankAccount.accountName || tx.bankAccount.accountNumber}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                        {cleanDescription}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {tx.imageUrl ? (
                          <a href={tx.imageUrl} target="_blank" rel="noopener noreferrer">
                            <img 
                              src={tx.imageUrl} 
                              alt="Bukti" 
                              className="w-10 h-10 object-cover rounded-lg border border-gray-200 hover:border-blue-400 transition-colors cursor-pointer mx-auto"
                            />
                          </a>
                        ) : (
                          <div className="w-10 h-10 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
                            <ImageIcon className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-gray-700">
                        {unitCount}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium text-gray-900">
                          {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(tx.amount)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEditClick(tx)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors font-medium text-xs"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Edit Transaction Modal */}
      {editingTransaction && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setEditingTransaction(null)}
          />
          <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl animate-slideUp sm:animate-scaleIn max-h-[90vh] sm:max-h-[85vh] flex flex-col">
            <div className="border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between shrink-0">
              {/* Mobile drag indicator */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
              <div className="pt-2 sm:pt-0">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Edit Transaksi</h3>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Perbaiki dan ajukan ulang transaksi</p>
              </div>
              <button 
                onClick={() => setEditingTransaction(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors touch-target"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {/* Image Upload/Replace */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bukti Transaksi</label>
                <input
                  type="file"
                  ref={editFileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleEditImageUpload}
                />
                {editImageUrl ? (
                  <div className="relative group">
                    <img 
                      src={editImageUrl} 
                      alt="Bukti Transaksi" 
                      className="w-full max-h-40 sm:max-h-48 object-contain rounded-xl border border-gray-200 bg-gray-50"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl items-center justify-center gap-2 hidden sm:flex">
                      <button
                        type="button"
                        onClick={() => editFileInputRef.current?.click()}
                        disabled={isUploadingEditImage}
                        className="px-3 py-1.5 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                      >
                        {isUploadingEditImage ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Ganti Gambar'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditImageUrl(null)}
                        className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
                      >
                        Hapus
                      </button>
                    </div>
                    {/* Mobile action buttons */}
                    <div className="flex gap-2 mt-2 sm:hidden">
                      <button
                        type="button"
                        onClick={() => editFileInputRef.current?.click()}
                        disabled={isUploadingEditImage}
                        className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium touch-target"
                      >
                        {isUploadingEditImage ? (
                          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                        ) : (
                          'Ganti Gambar'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditImageUrl(null)}
                        className="flex-1 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium touch-target"
                      >
                        Hapus
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => editFileInputRef.current?.click()}
                    disabled={isUploadingEditImage}
                    className="w-full h-28 sm:h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-blue-400 hover:bg-blue-50 transition-colors touch-target"
                  >
                    {isUploadingEditImage ? (
                      <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-gray-400" />
                        <span className="text-sm text-gray-500">Klik untuk unggah gambar</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <Input
                label="Tanggal Saldo"
                type="date"
                startIcon={Calendar}
                value={editFormData.date}
                onChange={(e) => setEditFormData({...editFormData, date: e.target.value})}
                required
              />

              <Select
                label="Produk Reksadana"
                startIcon={PieChart}
                value={editFormData.bankAccountId}
                onChange={(e) => setEditFormData({...editFormData, bankAccountId: e.target.value})}
              >
                <option value="">Pilih Produk Investasi...</option>
                {companies
                  .find(c => c.id === editingTransaction.company_id)
                  ?.bankAccounts?.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.bankName} - {acc.accountName || acc.accountNumber}</option>
                  ))}
              </Select>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <Input
                  label={`Total Nilai Investasi (${selectedCurrency.code})`}
                  type="number"
                  step="0.01"
                  min="0"
                  startText={selectedCurrency.symbol}
                  value={editFormData.amount}
                  onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})}
                  required
                />

                <Input
                  label="Jumlah Unit"
                  type="number"
                  step="0.0001"
                  min="0"
                  startIcon={Hash}
                  value={editFormData.unitCount}
                  onChange={(e) => setEditFormData({...editFormData, unitCount: e.target.value})}
                  placeholder="0.0000"
                />
              </div>

              <Input
                label="Keterangan"
                type="text"
                startIcon={FileText}
                value={editFormData.description}
                onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                required
              />
            </form>

            {/* Fixed bottom buttons on mobile */}
            <div className="flex gap-3 p-4 sm:px-6 sm:pb-6 border-t sm:border-t-0 border-gray-100 shrink-0 pb-safe">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditingTransaction(null)}
                className="flex-1"
                fullWidthMobile
              >
                Batal
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmittingEdit}
                className="flex-1"
                fullWidthMobile
                onClick={handleEditSubmit}
              >
                {isSubmittingEdit ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Ajukan Ulang
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
